import { resolveCardTeaser } from '../../../core/utils/card-teaser.util';

/**
 * Generic section adapter. All sections share wrapper fields (background,
 * overline, heading, subtitle) and may contain inner blocks via:
 *   - `blocks[]` dynamic zone
 *   - `cardsBlock` component → synthesised as blocks.cards-block
 *   - `ctaBlock` component → synthesised as blocks.cta-block
 *   - `textBlock` component → synthesised as blocks.text-block
 *
 * The adapter extracts wrapper fields and flattens all inner content
 * into a single `blocks[]` array for DynamicSectionComponent to render.
 */
export function adaptDynamicSection(
  entry: Record<string, unknown>,
  _baseUrl: string,
): Record<string, unknown> {
  const blocks: Record<string, unknown>[] = [];

  // Dedicated sub-components → synthesise as block entries
  const cardsBlock = entry['cardsBlock'] as Record<string, unknown> | undefined;
  if (cardsBlock) {
    blocks.push({ __component: 'blocks.cards-block', ...cardsBlock });
  }

  const ctaBlock = entry['ctaBlock'] as Record<string, unknown> | undefined;
  if (ctaBlock) {
    blocks.push({ __component: 'blocks.cta-block', ...ctaBlock });
  }

  const textBlock = entry['textBlock'] as Record<string, unknown> | undefined;
  if (textBlock) {
    blocks.push({ __component: 'blocks.text-block', ...textBlock });
  }

  /**
   * FAQ branch — merges inline `items` and the populated `faqEntries` relation
   * into a single flat list. Inline first, then relation entries. The reused
   * FaqBlockComponent renders the result unchanged (no fork, no new variant,
   * no category grouping in the embedded context). Phase 25, FAQS-01/02/03.
   */
  const rawInlineItems = entry['items'];
  const inlineItems: Record<string, unknown>[] = Array.isArray(rawInlineItems)
    ? (rawInlineItems as Record<string, unknown>[])
        .map((it) => ({
          question: (it['question'] as string) ?? '',
          answer: (it['answer'] as string) ?? '',
        }))
        .filter((it) => it.question !== '' || it.answer !== '')
    : [];

  const rawFaqEntries = entry['faqEntries'];
  const mappedEntries: Record<string, unknown>[] = Array.isArray(rawFaqEntries)
    ? (rawFaqEntries as Record<string, unknown>[])
        .map((rawEntry) => ({
          question: (rawEntry['question'] as string) ?? '',
          answer: (rawEntry['answer'] as string) ?? '',
        }))
        .filter((e) => e.question !== '' || e.answer !== '')
    : [];

  const mergedFaqItems = [...inlineItems, ...mappedEntries];
  if (mergedFaqItems.length > 0) {
    blocks.push({ __component: 'blocks.faq-block', items: mergedFaqItems });
  }

  // Resolved relation → synthesise as blocks.cards-block
  const resolvedRelation = entry['_resolvedRelation'] as Record<string, unknown>[] | undefined;
  if (Array.isArray(resolvedRelation) && resolvedRelation.length > 0) {
    const basePath = (entry['_relationBasePath'] as string) ?? '';
    const relField = (entry['relationField'] as string) ?? '';
    const isCaseStudy = relField === 'relatedCaseStudies' || relField === 'caseStudies';
    // Asset-type relations (Spec 28): "Related asset types" on an asset-type-page
    // and "Assets monitored" on an industry-page both map to asset-type-page.
    const isAssetType = relField === 'relatedAssetTypes' || relField === 'assetTypes';

    blocks.push({
      __component: 'blocks.cards-block',
      columns: entry['columns'] ?? 3,
      cards: resolvedRelation.map((item: Record<string, unknown>) => {
        const slug = (item['slug'] as string) ?? '';

        // Image: case studies use featuredImage, asset-type uses top-level cardImage
        // (Spec 28 — original URL; Strapi's 156px `thumbnail` format is too small
        // for a ~200-300px card slot at 2x DPI), others use hero.backgroundImage.
        let imageSrc: Record<string, unknown> | null;
        let imageAlt: string;
        let useThumbImage = false;
        if (isCaseStudy) {
          const featured = item['featuredImage'] as Record<string, unknown> | undefined;
          imageSrc = featured ?? null;
          imageAlt = (featured?.['alternativeText'] as string) ?? '';
        } else if (isAssetType) {
          const cardImage = item['cardImage'] as Record<string, unknown> | undefined;
          imageSrc = cardImage && cardImage['url'] ? cardImage : null;
          imageAlt = (cardImage?.['alternativeText'] as string) || ((item['title'] as string) ?? '');
          useThumbImage = true;
        } else {
          const hero = item['hero'] as Record<string, unknown> | undefined;
          const bgImage = hero?.['backgroundImage'] as Record<string, unknown> | undefined;
          imageSrc = bgImage ?? null;
          imageAlt = (bgImage?.['alternativeText'] as string) ?? '';
        }

        // Decorator: show industry for case studies
        const industries = item['industries'] as Array<Record<string, unknown>> | undefined;
        const industryName = industries?.[0]?.['title'] as string | undefined;
        const decorator = isCaseStudy && industryName
          ? { type: 'label', value: industryName }
          : undefined;

        const teaser = resolveCardTeaser(
          item as { cardDescription?: string | null; seo?: { metaDescription?: string | null } | null },
        );

        // For asset-type we pass `variant: 'thumb-image'` explicitly so the
        // downstream cards-block adapter does NOT auto-promote 'base' → 'image'
        // (promotion only fires when rawVariant === 'base', so an explicit
        // 'thumb-image' passes through untouched).
        return {
          ...(useThumbImage ? { variant: 'thumb-image' } : {}),
          subhead: (item['title'] as string) ?? '',
          ...(teaser ? { body: teaser } : {}),
          decorator,
          link: {
            href: basePath ? `${basePath}/${slug}` : `/${slug}`,
            text: isCaseStudy ? 'View case study' : 'Learn more',
            style: 'arrow',
          },
          imageSrc,
          imageAlt,
        };
      }),
    });
  }

  // Dynamic zone blocks
  const dzBlocks = entry['blocks'];
  if (Array.isArray(dzBlocks)) {
    blocks.push(...dzBlocks);
  }

  return {
    sectionId: (entry['htmlId'] as string) ?? (entry['sectionId'] as string) ?? (entry['anchorId'] as string) ?? '',
    background: (entry['background'] as string) ?? '',
    overline: (entry['overline'] as string) ?? '',
    heading: (entry['heading'] as string) ?? '',
    subtitle: (entry['subtitle'] as string) ?? '',
    linkText: (entry['linkText'] as string) ?? '',
    linkHref: (entry['linkHref'] as string) ?? '',
    cssClass: (entry['cssClass'] as string) ?? '',
    blocks,
  };
}
